import { UserAvatar } from "@/components/user-avatar";
import { Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Bell,
  Heart,
  LayoutDashboard,
  LifeBuoy,
  LogOut,
  MessageCircle,
  ChevronRight,
  ShieldCheck,
  Store,
  User,
  Wallet,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { supabase } from "@/integrations/supabase/client";
import type { User as SupabaseUser } from "@supabase/supabase-js";

const items = [
  { to: "/profil", label: "Kabinet", icon: LayoutDashboard },
  { to: "/magazam", label: "Mağaza yarat", icon: Store },
  { to: "/balans", label: "Balans", icon: Wallet },
  { to: "/secilmisler", label: "Seçilmişlər", icon: Heart },
  { to: "/mesajlar", label: "Mesajlar", icon: MessageCircle },
  { to: "/bildirisler", label: "Bildirişlər", icon: Bell },
  { to: "/elaqe", label: "Dəstək / Adminə yaz", icon: LifeBuoy },
] as const;

export function useStaffRole(userId: string | undefined) {
  const { data } = useQuery({
    queryKey: ["user-role", userId],
    enabled: Boolean(userId),
    queryFn: async () => {
      const [{ data: admin }, { data: moderator }] = await Promise.all([
        supabase.rpc("has_role", { _user_id: userId!, _role: "admin" }),
        supabase.rpc("has_role", { _user_id: userId!, _role: "moderator" }),
      ]);
      return admin ? "admin" : moderator ? "moderator" : "user";
    },
  });
  return data;
}

export function UserMenu({ user }: { user: SupabaseUser }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const role = useStaffRole(user.id);

  const { data: profile } = useQuery({
    queryKey: ["profile-avatar", user.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("profiles")
        .select("full_name, avatar_url")
        .eq("id", user.id)
        .maybeSingle();
      return data;
    },
  });

  const isStaff = role === "admin" || role === "moderator";
  const name =
    profile?.full_name?.trim() ||
    (user.user_metadata?.["full_name"] as string | undefined) ||
    user.email ||
    "İstifadəçi";

  const signOut = async () => {
    await queryClient.cancelQueries();
    queryClient.clear();
    await supabase.auth.signOut();
    navigate({ to: "/giris", replace: true });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Profilim"
          title="Profilim"
          className="grid size-9 shrink-0 place-items-center overflow-hidden rounded-full border border-border bg-card transition-all hover:ring-2 hover:ring-primary/50 md:size-10"
        >
          {profile?.avatar_url ? (
            <UserAvatar src={profile.avatar_url} name={name} className="size-full border-0" />
          ) : (
            <User className="size-4 text-primary" />
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={8}
        className="w-[min(18rem,calc(100vw-1.5rem))] rounded-2xl border-border/70 p-2 shadow-xl"
      >
        <DropdownMenuLabel className="p-0">
          <Link
            to="/istifadeci/$id"
            params={{ id: user.id }}
            className="flex items-center gap-2.5 rounded-xl p-2 transition-colors hover:bg-accent"
          >
            <UserAvatar src={profile?.avatar_url} name={name} className="size-10" online />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-bold text-foreground">{name}</span>
              <span className="block text-[11px] font-medium text-primary">Profilə bax</span>
            </span>
            <ChevronRight className="size-3.5 shrink-0 text-muted-foreground" />
          </Link>
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="my-1.5" />
        {items.map((i) => (
          <DropdownMenuItem key={i.label} asChild className="rounded-xl px-2.5 py-2 text-xs font-semibold">
            <Link to={i.to}>
              <i.icon className="size-3.5 shrink-0 text-muted-foreground" />
              <span className="truncate">{i.label}</span>
            </Link>
          </DropdownMenuItem>
        ))}
        {isStaff && (
          <>
            <DropdownMenuSeparator className="my-1.5" />
            <DropdownMenuItem asChild className="rounded-xl px-2.5 py-2 text-xs font-semibold text-primary focus:text-primary">
              <Link to="/admin">
                <ShieldCheck className="size-3.5 shrink-0" />{" "}
                {role === "admin" ? "Admin panel" : "Moderator panel"}
              </Link>
            </DropdownMenuItem>
          </>
        )}
        <DropdownMenuSeparator className="my-1.5" />
        <DropdownMenuItem
          onSelect={signOut}
          className="rounded-xl px-2.5 py-2 text-xs font-semibold text-destructive focus:text-destructive"
        >
          <LogOut className="size-3.5 shrink-0" /> Çıxış
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}